import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useReactive } from 'ahooks';
import { Button, Col, Input, Popconfirm, Row, Space } from 'antd';

const { TextArea } = Input;
import { map } from 'lodash';
import { TPicture } from 'types/picture';
import { ZodDefault, ZodError, ZodNullable, ZodOptional, ZodTypeAny } from 'zod';

import { ProfileItem } from '@atoms/profileItem';
import { ProfileSkeleton } from '@atoms/skeletons/profileSkeleton';
import { Common } from '@classes/commonMethods';
import { messageService } from '@classes/messageService';
import { SettingsManager } from '@classes/settingsManager';
import RenderIf from '@common/RenderIf';
import { SkeletonWrapper } from '@common/SkeletonWrapper/SkeletonWrapper';
import { NotFoundWithBack } from '@components/notFoundWithBack/notFoundWithBack';
import {
    faCheck,
    faEdit,
    faKeyboard,
    faMicrophone,
    faPause,
    faPersonRunning,
    faStop,
    faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFetchDataQuery } from '@hooks/useFetchDataQuery';
import { useBatchImages } from '@hooks/useImageBatch';
import { HeaderBreadcrumbProfiles } from '@molecules/breadcrumbs/profilesBreadcrumbs/HeaderProfilesBreadcrumb';
import { FunctionalMenu } from '@molecules/functionalMenu';
import { ImgUploader } from '@organisms/imgUploader/imgUploader';
import { useAudioRecorder } from '@hooks/useAudioRecorder';
import { useReturnTo } from '@hooks/useReturnTo';
import { createLocalFileId } from '@utils/createLocalFileId';
import { DurationPicker } from '@organisms/pickers/durationPicker';
import { ProductPicker } from '@organisms/pickers/productPicker';
import { ServicePicker } from '@organisms/pickers/servicePicker';
import { TaskCategoryPicker } from '@organisms/pickers/taskCategoryPicker';
import { LastIdStore } from '@pages/lastIdStore';
import { capitalizeFirstLetter } from '@utils/capitalizeFirstLetter';
import { imagePicker } from '@utils/imagePicker';

import { createTask, updateTask } from '../api';
import { TTaskCategory } from '../common/types';
import { TClientTask, TaskSchema } from '../types';

import './style.scss';

// activity_category_id заполнен только у админских базовых задач — для
// обычной задачи бэк отдаёт null, а TaskSchema ждёт number|undefined.
// Вместо валидации/отправки null просто убираем ключ, чтобы поле не летело
// в запрос на создание/обновление задачи.
const omitNullActivityCategoryId = (data: any) => {
    const task = { ...data };
    if (task.activity_category_id == null) {
        delete task.activity_category_id;
    }
    return task;
};

export enum TaskUseCases {
    Profile = 'profile',
    Edit = 'edit',
    Create = 'create',
}

type TProps = {
    useCase?: TaskUseCases;
};

type TState = {
    isLoadingState: boolean;
    isDurationPickerVisible: boolean;
    isProductPickerVisible: boolean;
    isServicePickerVisible: boolean;
    isTaskCategoryPickerVisible: boolean;
    case: TaskUseCases;
    task?: TClientTask & { category: TTaskCategory };
    recordingTarget?: 'task_statement' | 'description';
};

const TaskProfile = ({ useCase }: TProps): JSX.Element => {
    const state = useReactive<TState>({
        isLoadingState: false,
        isDurationPickerVisible: false,
        isProductPickerVisible: false,
        isServicePickerVisible: false,
        isTaskCategoryPickerVisible: false,
        task: null,
        case: useCase ?? TaskUseCases.Profile,
        recordingTarget: null,
    });
    const {
        isRecording,
        isPaused,
        time,
        start: startRecorder,
        pause: pauseRecording,
        resume: resumeRecording,
        stop: stopRecorder,
    } = useAudioRecorder({
        onRecorded: ({ blob, duration }) => {
            const target = state.recordingTarget;
            if (!target) return;

            state.task[target] = {
                id: createLocalFileId(),
                name: target === 'task_statement' ? 'Постановка' : 'Описание',
                extension: 'audio',
                blob,
                duration,
            };
        },
    });
    const { taskID } = useParams();
    const { returnTo, goBack } = useReturnTo();
    const [progress, setProgress] = useState<{ percentage: number; file: File } | null>(null);

    const navigate = useNavigate();

    const { addImages, removeImages } = useBatchImages((percentage, file) => {
        setProgress({ percentage, file });
    });

    const location = useLocation();
    const creds = SettingsManager.getConnectionCredentials();

    const editing = state.case != TaskUseCases.Profile;

    const template = location.state?.service;

    const taskDefault = TaskSchema.parse({});

    const normalizedPictures = map(state.task?.pictures, (picture) =>
        imagePicker({ picture: picture })
    );

    const {
        data: taskData,
        isLoading: isTaskLoading,
        refetch: refetchTask,
        error: taskError,
    } = useFetchDataQuery({
        getFnKey: 'getTask',
        params: [creds.crmID, Number(taskID)],
        enabled: state.case !== TaskUseCases.Create,
        setDataList: (data) => {
            state.task = omitNullActivityCategoryId(data);
        },
    });

    // TODO: делегировать в компонент ProfileItem
    const isRequired = (fieldName: keyof typeof TaskSchema.shape) => {
        let schema: ZodTypeAny = TaskSchema.shape[fieldName];

        while (true) {
            if (schema instanceof ZodOptional || schema instanceof ZodNullable) {
                return false;
            }
            if (schema instanceof ZodDefault) {
                schema = (schema as any)._def.innerType;
            } else {
                break;
            }
        }

        return true;
    };

    const validate = (): boolean => {
        if (isRecording) return false;
        try {
            TaskSchema.parse(state.task);
            return true;
        } catch (err) {
            if (err instanceof ZodError) {
                const messages = err.issues.map((issue) => issue.message);
                messageService.sendErrorList(messages);
            } else {
                messageService.sendError('Ошибка при валидации задачи');
            }
            return false;
        }
    };

    function handleEdit() {
        state.case = TaskUseCases.Edit;
        navigate(`/lk/worker/crm/${creds.crmID}/tasks/${taskID}/edit`);
    }

    async function handleSave() {
        if (!validate()) return;
        state.isLoadingState = true;

        let updatedTask = null;

        try {
            if (state.case == TaskUseCases.Create) {
                updatedTask = await createTask(state.task);
            } else {
                updatedTask = await updateTask(state.task);
            }
            if (!updatedTask) throw Error('Ошибка при обновлении задачи');
            state.task = updatedTask;

            state.case = TaskUseCases.Profile;

            // Пришли с другой страницы (например из заказа) — возвращаемся туда
            if (returnTo) {
                goBack();
            } else {
                navigate(`/lk/worker/crm/${creds.crmID}/tasks/${updatedTask.id}`);
            }
        } catch (errors) {
            messageService.sendErrorList(errors);
        } finally {
            await refetchTask();
        }
        state.isLoadingState = false;
    }

    async function handleCancel() {
        if (isRecording) stopRecording({ preventDefault: () => null });
        if (returnTo) {
            goBack();
        } else if (state.case == TaskUseCases.Create)
            navigate(`/lk/worker/crm/${creds.crmID}/tasks`);
        else {
            state.case = TaskUseCases.Profile;
            navigate(`/lk/worker/crm/${creds.crmID}/tasks/${taskID}`);
            state.task = omitNullActivityCategoryId(taskData);
        }
    }

    const setEmptyTask = () => {
        state.task = {
            ...taskDefault,
            crm_id: creds.crmID,
            task_category_id: LastIdStore.lastTaskCategoryId,
            ...template,
        };
    };

    async function handleImageSelect(file: File): Promise<void> {
        state.task.pictures = [...(state.task.pictures as File[]), file];
        const uploaded = await addImages('tasks', +taskID, [file]);
        const newPictures = uploaded?.pictures || [];
        state.task.pictures = [...newPictures];
    }

    async function handleImageDelete(index: number): Promise<void> {
        const pictureId = (state.task.pictures as any[])[index]?.id;
        if (pictureId) {
            await removeImages('tasks', +taskID, [pictureId]);
            state.task.pictures = (state.task.pictures as any[]).filter((_, i) => i !== index);
        }
    }

    const startRecording = (e, target: 'task_statement' | 'description') => {
        e.preventDefault();
        if (isRecording) return;

        state.recordingTarget = target;
        startRecorder();
    };

    const stopRecording = (e) => {
        e.preventDefault();
        stopRecorder();
        state.recordingTarget = null;
    };

    useEffect(() => {
        if (state.case === TaskUseCases.Create) {
            setEmptyTask();
        }
    }, []);

    const isLoading = useMemo(
        () => state.isLoadingState || isTaskLoading,
        [state.isLoadingState, isTaskLoading]
    );

    if (taskError?.statusCode) {
        return (
            <NotFoundWithBack
                backPath={`/lk/worker/crm/${creds?.crmID}/tasks`}
                statusCode={taskError.statusCode}
            />
        );
    }

    return (
        <div className="profile-template task-profile">
            {state.isDurationPickerVisible && (
                <DurationPicker
                    isModalOpen={state.isDurationPickerVisible}
                    onCancel={() => {
                        state.isDurationPickerVisible = false;
                    }}
                    defaultValue={(state.task?.duration || 0) / 60}
                    totalTime={state.task?.total_time}
                    onOk={(duration, total_time) => {
                        // DurationPicker работает в минутах, а бэк хранит duration в секундах
                        state.task.duration = duration * 60;
                        state.task.total_time = total_time;
                        state.isDurationPickerVisible = false;
                    }}
                />
            )}
            {state.isProductPickerVisible && (
                <ProductPicker
                    isModalOpen={state.isProductPickerVisible}
                    setIsModalOpen={(isOpen) => (state.isProductPickerVisible = isOpen)}
                    multiple={true}
                    selectedByDefault={state.task?.products}
                    setSelected={(selected) => (state.task.products = selected)}
                />
            )}
            {state.isServicePickerVisible && (
                <ServicePicker
                    isModalOpen={state.isServicePickerVisible}
                    setIsModalOpen={(isOpen) => (state.isServicePickerVisible = isOpen)}
                    multiple={true}
                    selectedByDefault={state.task?.services}
                    setSelected={(selected) => (state.task.services = selected)}
                />
            )}
            {state.isTaskCategoryPickerVisible && (
                <TaskCategoryPicker
                    isModalOpen={state.isTaskCategoryPickerVisible}
                    setIsModalOpen={(isOpen) => (state.isTaskCategoryPickerVisible = isOpen)}
                    multiple={false}
                    selectedByDefault={
                        state.task?.task_category_id == null
                            ? []
                            : [
                                  {
                                      id: state.task?.task_category_id,
                                      name: state.task?.category?.name,
                                      parentId: state.task?.category?.parent_task_category_id,
                                  },
                              ]
                    }
                    setSelected={(selected) => {
                        if (selected.length > 0) state.task.task_category_id = selected[0].id;
                        else state.task.task_category_id = null;
                    }}
                />
            )}
            <FunctionalMenu
                items={
                    state.case != TaskUseCases.Profile
                        ? [
                              {
                                  key: 'save',
                                  label:
                                      state.case == TaskUseCases.Create ? 'Создать' : 'Сохранить',
                                  icon: <FontAwesomeIcon icon={faCheck} />,
                                  onClick: handleSave,
                              },
                              {
                                  key: 'abort',
                                  label: 'Отменить',
                                  icon: <FontAwesomeIcon icon={faXmark} />,
                                  onClick: handleCancel,
                              },
                          ]
                        : [
                              {
                                  key: 'edit',
                                  label: 'Редактировать',
                                  icon: <FontAwesomeIcon icon={faEdit} />,
                                  onClick: handleEdit,
                              },
                          ]
                }
                dropdownItems={[]}
            />
            <HeaderBreadcrumbProfiles
                dataIcon={faPersonRunning}
                dataId={state.task?.id}
                dataTitle={'Задачи'}
                title={'Профиль задачи'}
                route={`/lk/worker/crm/${creds.crmID}/tasks`}
                isForInvitation={false}
                dataName={'Новая задача'}
                isSpecialty={false}
            />
            <SkeletonWrapper isLoading={isLoading} skeleton={<ProfileSkeleton />}>
                <div className="profile-inner">
                    <Row className="profile-row">
                        <Col className="avatar outer-box" flex={1}>
                            <ImgUploader
                                picture={imagePicker({ picture: state.task?.picture as TPicture })}
                                pictures={normalizedPictures}
                                onImageSelect={handleImageSelect}
                                isEdit={state.case != TaskUseCases.Profile}
                                onImageDelete={handleImageDelete}
                                progress={progress}
                            />
                        </Col>
                        <Col className="outer-box" flex={1}>
                            <ProfileItem
                                required={isRequired('name')}
                                validator={TaskSchema.shape.name}
                                label="Название"
                                value={state.task?.name}
                                isEdit={editing}
                                onChange={(value) =>
                                    (state.task.name = capitalizeFirstLetter(value))
                                }
                                inputType="textarea"
                            />
                        </Col>
                        <Col className="outer-box" flex={1}>
                            <ProfileItem
                                required={isRequired('duration')}
                                validator={TaskSchema.shape.duration}
                                label="Продолжительность"
                                value={state.task?.duration}
                                isEdit={editing}
                                valueRender={(value, meta) => (
                                    <>
                                        <Col>
                                            {editing && (
                                                <Button
                                                    type="primary"
                                                    shape="circle"
                                                    icon={<FontAwesomeIcon icon={faEdit} />}
                                                    onClick={() => {
                                                        state.isDurationPickerVisible = true;
                                                    }}
                                                />
                                            )}
                                        </Col>
                                        <Col>
                                            <p>
                                                {value == 0
                                                    ? 'Не задана'
                                                    : Common.convertMinutes2Date(value / 60)}
                                            </p>
                                            <p>
                                                Учитывается
                                                {state.task?.total_time
                                                    ? ' общее время'
                                                    : ' только рабочее время'}
                                            </p>
                                        </Col>
                                    </>
                                )}
                                viewRender={(value, meta) => (
                                    <>
                                        <Col>
                                            <p>
                                                {value === 0
                                                    ? 'Не задана'
                                                    : Common.convertMinutes2Date(value / 60)}
                                            </p>
                                            <p>
                                                Учитывается
                                                {state.task?.total_time
                                                    ? ' общее время'
                                                    : ' только рабочее время'}
                                            </p>
                                        </Col>
                                    </>
                                )}
                            />
                        </Col>
                    </Row>
                    <Row className="profile-row">
                        <Col className="outer-box" flex={1}>
                            <ProfileItem
                                required={isRequired('reward')}
                                validator={TaskSchema.shape.reward}
                                label="На баланс исполнителя"
                                value={state.task?.reward}
                                isEdit={editing}
                                onChange={(value) =>
                                    (state.task.reward =
                                        value === '' ? 0 : Number.parseFloat(value))
                                }
                                inputProps={{
                                    type: 'number',
                                    inputMode: 'numeric',
                                    min: 0,
                                    max: 9999999,
                                    step: 0.01,
                                }}
                            />
                        </Col>
                        <Col className="outer-box" flex={1}>
                            <ProfileItem
                                required={isRequired('task_category_id')}
                                validator={TaskSchema.shape.task_category_id}
                                label="Группа"
                                value={state.task?.task_category_id}
                                isEdit={editing}
                                valueRender={(value, meta) => (
                                    <Col>
                                        {editing ? (
                                            <a
                                                style={{ color: !meta.isValid ? 'red' : undefined }}
                                                onClick={() => {
                                                    if (state.case != TaskUseCases.Profile)
                                                        state.isTaskCategoryPickerVisible = true;
                                                }}
                                            >
                                                {state.task?.category?.name || 'Не выбрана'}
                                            </a>
                                        ) : (
                                            <p>{state.task?.category?.name || 'Не выбрана'}</p>
                                        )}
                                    </Col>
                                )}
                            />
                        </Col>
                    </Row>
                    <Row className="profile-row">
                        <Col className="outer-box" flex={1}>
                            <ProfileItem
                                required={isRequired('min_performers')}
                                validator={TaskSchema.shape.min_performers}
                                label="Мин. исполнителей"
                                value={state.task?.min_performers}
                                isEdit={editing}
                                onChange={(value) => (state.task.min_performers = +value)}
                                inputProps={{
                                    type: 'number',
                                    inputMode: 'numeric',
                                    min: 0,
                                    max: 10,
                                    step: 1,
                                }}
                            />
                        </Col>
                        <Col className="outer-box" flex={1}>
                            <Row className="label-row">
                                <Col>
                                    <p>Прослушать задачу</p>
                                </Col>
                            </Row>
                            <Row className="value-row">
                                <Col>
                                    <RenderIf condition={state.case == TaskUseCases.Profile}>
                                        <RenderIf condition={state.task?.task_statement == null}>
                                            <p>Не задано</p>
                                        </RenderIf>
                                        {/*<RenderIf condition={state.task?.task_statement != null}>
                                        <FileThumbnail
                                            index={0}
                                            files={[state.task?.task_statement]}
                                            file={state.task?.task_statement}
                                            key={state.task?.task_statement?.id}
                                        />
                                    </RenderIf>*/}
                                    </RenderIf>
                                    <RenderIf condition={state.case != TaskUseCases.Profile}>
                                        {/*<RenderIf condition={state.task?.task_statement != null}>
                                        <FileThumbnail
                                            index={0}
                                            files={[state.task?.task_statement]}
                                            file={state.task?.task_statement}
                                            key={state.task?.task_statement?.id}
                                            removeFile={(_) => state.task.task_statement = null}
                                        />
                                    </RenderIf>*/}
                                        <RenderIf condition={state.task?.task_statement == null}>
                                            <RenderIf
                                                condition={
                                                    isRecording &&
                                                    state.recordingTarget == 'task_statement'
                                                }
                                            >
                                                <Row className="comment-block" align={'middle'}>
                                                    <Col>
                                                        <div className="stopwatch-display">
                                                            <h3>Запись аудио</h3>
                                                            <h2>
                                                                {time}
                                                            </h2>
                                                        </div>
                                                    </Col>
                                                    <Col className="voice-btn">
                                                        <Space direction="vertical">
                                                            <RenderIf condition={!isPaused}>
                                                                <Button
                                                                    onClick={pauseRecording}
                                                                    icon={
                                                                        <FontAwesomeIcon
                                                                            icon={faPause}
                                                                            color="orange"
                                                                            size="lg"
                                                                        />
                                                                    }
                                                                ></Button>
                                                            </RenderIf>
                                                            <RenderIf condition={isPaused}>
                                                                <Button
                                                                    onClick={resumeRecording}
                                                                    icon={
                                                                        <FontAwesomeIcon
                                                                            icon={faMicrophone}
                                                                            color="orange"
                                                                            size="lg"
                                                                        />
                                                                    }
                                                                ></Button>
                                                            </RenderIf>
                                                            <Button
                                                                onClick={stopRecording}
                                                                icon={
                                                                    <FontAwesomeIcon
                                                                        icon={faStop}
                                                                        color="orange"
                                                                        size="lg"
                                                                    />
                                                                }
                                                            ></Button>
                                                        </Space>
                                                    </Col>
                                                </Row>
                                            </RenderIf>
                                            <RenderIf
                                                condition={
                                                    !isRecording ||
                                                    (isRecording &&
                                                        state.recordingTarget == 'description')
                                                }
                                            >
                                                <Button
                                                    onClick={(e) =>
                                                        startRecording(e, 'task_statement')
                                                    }
                                                    shape="circle"
                                                    className="comment-type-select"
                                                >
                                                    <span className="comment-type-select">+</span>
                                                    {
                                                        <FontAwesomeIcon
                                                            icon={faMicrophone}
                                                            color="orange"
                                                            size="lg"
                                                        />
                                                    }
                                                </Button>
                                            </RenderIf>
                                        </RenderIf>
                                    </RenderIf>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <Row className="profile-wide outer-box description-tab">
                        <Col>
                            <Row className="label-row">
                                <Col>
                                    <p>Суть задачи, описание</p>
                                </Col>
                                <RenderIf condition={state.case != TaskUseCases.Profile}>
                                    <Col>
                                        <Popconfirm
                                            title="Смена типа описания - введённые данные удалятся"
                                            onConfirm={() => {
                                                // TODO: FIX
                                                return null;
                                                if (state.task?.description_type == 'text')
                                                    state.task.description_type = 'voice';
                                                else state.task.description_type = 'text';
                                                state.task.description = null;
                                            }}
                                            okText="Да"
                                            cancelText="Нет"
                                            disabled={
                                                state.task?.description == null ||
                                                state.task?.description == ''
                                            }
                                        >
                                            <Button
                                                className="description-type-select"
                                                icon={
                                                    <FontAwesomeIcon
                                                        icon={
                                                            state.task?.description_type == 'text'
                                                                ? faKeyboard
                                                                : faMicrophone
                                                        }
                                                    />
                                                }
                                                onClick={() => {
                                                    // TODO: FIX
                                                    return null;
                                                    if (
                                                        state.task?.description == null ||
                                                        state.task?.description == ''
                                                    ) {
                                                        if (state.task?.description_type == 'text')
                                                            state.task.description_type = 'voice';
                                                        else state.task.description_type = 'text';
                                                        state.task.description = null;
                                                    }
                                                }}
                                                shape="circle"
                                            />
                                        </Popconfirm>
                                    </Col>
                                </RenderIf>
                            </Row>
                            <Row className="value-row">
                                <Col>
                                    <RenderIf condition={state.case == TaskUseCases.Profile}>
                                        <RenderIf
                                            condition={state.task?.description_type == 'text'}
                                        >
                                            <p>{state.task?.description as string}</p>
                                        </RenderIf>
                                        <RenderIf
                                            condition={state.task?.description_type == 'voice'}
                                        >
                                            {
                                                state.task?.description == null ? (
                                                    <p>Не задано</p>
                                                ) : null /*(
                                            <FileThumbnail
                                                index={0}
                                                files={[state.task?.description as FileType]}
                                                file={state.task?.description as FileType}
                                                key={(state.task?.description as FileType)?.id}
                                            />
                                        )*/
                                            }
                                        </RenderIf>
                                    </RenderIf>
                                    <RenderIf condition={state.case != TaskUseCases.Profile}>
                                        <RenderIf
                                            condition={state.task?.description_type == 'text'}
                                        >
                                            <TextArea
                                                autoSize={{ minRows: 2, maxRows: 5 }}
                                                maxLength={255}
                                                onChange={(e) => {
                                                    state.task.description = e.target.value;
                                                }}
                                                value={state.task?.description as string}
                                            />
                                        </RenderIf>
                                        {/*<RenderIf condition={state.task?.description_type == 'voice'}>
                                        <RenderIf condition={isRecording && state.recordingTarget == 'description'}>
                                            <Row
                                                className="comment-block"
                                                align={'middle'}
                                            >
                                                <Col>
                                                    <div className="stopwatch-display">
                                                        <h3>Запись аудио</h3>
                                                        <h2>
                                                            {time}
                                                        </h2>
                                                    </div>
                                                </Col>
                                                <Col className="voice-btn">
                                                    <Space direction="vertical">
                                                        <RenderIf condition={!isPaused}>
                                                            <Button
                                                                onClick={pauseRecording}
                                                                icon={
                                                                    <FontAwesomeIcon
                                                                        icon={faPause}
                                                                        color="orange"
                                                                        size="lg"
                                                                    />
                                                                }
                                                            ></Button>
                                                        </RenderIf>
                                                        <RenderIf condition={isPaused}>
                                                            <Button
                                                                onClick={resumeRecording}
                                                                icon={
                                                                    <FontAwesomeIcon
                                                                        icon={faMicrophone}
                                                                        color="orange"
                                                                        size="lg"
                                                                    />
                                                                }
                                                            ></Button>
                                                        </RenderIf>
                                                        <Button
                                                            onClick={stopRecording}
                                                            icon={
                                                                <FontAwesomeIcon icon={faStop} color="orange" size="lg" />
                                                            }
                                                        ></Button>
                                                    </Space>
                                                </Col>
                                            </Row>
                                        </RenderIf>
                                        <RenderIf condition={!isRecording || (isRecording && state.recordingTarget == 'task_statement')}>
                                            <RenderIf condition={state.task?.description == null}>
                                                <Button
                                                    onClick={(e) => startRecording(e, 'description')}
                                                    shape="circle"
                                                    className="comment-type-select"
                                                >
                                                    <span className="comment-type-select">+</span>
                                                    {<FontAwesomeIcon icon={faMicrophone} color="orange" size="lg" />}
                                                </Button>
                                            </RenderIf>
                                            <RenderIf condition={state.task?.description != null}>
                                                <FileThumbnail
                                                    index={0}
                                                    files={[state.task?.description as FileType]}
                                                    file={state.task?.description as FileType}
                                                    key={(state.task?.description as FileType)?.id}
                                                    removeFile={(_) => state.task.description = null}
                                                />
                                            </RenderIf>
                                        </RenderIf>
                                    </RenderIf>*/}
                                    </RenderIf>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>
            </SkeletonWrapper>
        </div>
    );
};

export default TaskProfile;
